/**
 * Meaningful Outcome Analytics Contract
 *
 * Privacy-safe analytics taxonomy for Losi outcome completion.
 *
 * Primary responsibilities:
 * - Define meaningful outcome event names and allowed properties
 * - Keep event values consistent across Chat, Spaces, Sites, Nexus, and Library
 * - Sanitize raw payloads so user-generated content never reaches analytics
 *
 * Dependencies:
 * - None
 */

export const MeaningfulOutcomeEventNames = {
  MEANINGFUL_OUTCOME_COMPLETED: 'meaningful_outcome_completed',
  FIRST_MEANINGFUL_OUTCOME_COMPLETED: 'first_meaningful_outcome_completed'
} as const

export const MEANINGFUL_OUTCOME_EVENT_NAMES = Object.values(
  MeaningfulOutcomeEventNames
)

export type MeaningfulOutcomeEventName =
  (typeof MEANINGFUL_OUTCOME_EVENT_NAMES)[number]

export const MEANINGFUL_OUTCOME_EVENT_NAME_SET: ReadonlySet<MeaningfulOutcomeEventName> =
  new Set(MEANINGFUL_OUTCOME_EVENT_NAMES)

export const MEANINGFUL_OUTCOME_SURFACES = [
  'chat',
  'spaces',
  'sites',
  'nexus',
  'library'
] as const

export type MeaningfulOutcomeSurface =
  (typeof MEANINGFUL_OUTCOME_SURFACES)[number]

export const MEANINGFUL_OUTCOME_TYPES = [
  'saved_output',
  'task',
  'note',
  'sheet',
  'doc',
  'site_brief',
  'site_draft',
  'landing_page_draft',
  'form_draft',
  'quiz_draft',
  'calculator_draft',
  'portal_draft',
  'mini_app_draft',
  'plan',
  'checklist',
  'crm_lead',
  'crm_activity',
  'communication',
  'follow_up',
  'workflow',
  'agent_action',
  'booking',
  'artifact'
] as const

export type MeaningfulOutcomeType =
  (typeof MEANINGFUL_OUTCOME_TYPES)[number]

export const MEANINGFUL_OUTCOME_ACTIONS = [
  'saved',
  'saved_to_space',
  'created',
  'draft_created',
  'completed',
  'marked_complete',
  'copied',
  'exported',
  'shared',
  'reopened',
  'published',
  'sent',
  'scheduled',
  'executed',
  'logged',
  'updated'
] as const

export type MeaningfulOutcomeAction =
  (typeof MEANINGFUL_OUTCOME_ACTIONS)[number]

export const MEANINGFUL_OUTCOME_CHANNELS = [
  'chat',
  'email',
  'sms',
  'voice',
  'web',
  'unknown'
] as const

export type MeaningfulOutcomeChannel =
  (typeof MEANINGFUL_OUTCOME_CHANNELS)[number]

export const MEANINGFUL_OUTCOME_SCOPES = [
  'workspace',
  'chat',
  'space',
  'folder',
  'task',
  'unknown'
] as const

export type MeaningfulOutcomeScope =
  (typeof MEANINGFUL_OUTCOME_SCOPES)[number]

export interface MeaningfulOutcomeProperties {
  surface: MeaningfulOutcomeSurface
  outcome_type: MeaningfulOutcomeType
  qualifying_action: MeaningfulOutcomeAction
  activity_id?: string
  activity_type?: string
  agent_id?: string
  artifact_id?: string
  booking_id?: string
  campaign_id?: string
  channel?: MeaningfulOutcomeChannel
  checklist_item_id?: string
  chat_id?: string
  communication_id?: string
  company_id?: string
  contact_id?: string
  doc_id?: string
  file_scope?: MeaningfulOutcomeScope
  folder_id?: string
  is_anonymous?: boolean
  is_first_meaningful_outcome?: boolean
  lead_id?: string
  message_id?: string
  note_id?: string
  occurred_at?: string
  opportunity_id?: string
  plan?: string
  resource_id?: string
  scope?: MeaningfulOutcomeScope
  service_id?: string
  session_id?: string
  sheet_id?: string
  site_id?: string
  site_version_id?: string
  source?: string
  space_id?: string
  sprint_id?: string
  task_id?: string
  user_id?: string
  workflow_id?: string
  workflow_instance_id?: string
  workspace_id?: string
}

export interface MeaningfulOutcomeAnalyticsEvent {
  event: MeaningfulOutcomeEventName
  properties: MeaningfulOutcomeProperties
}

const SAFE_IDENTIFIER_KEYS = [
  'activity_id',
  'agent_id',
  'artifact_id',
  'booking_id',
  'campaign_id',
  'checklist_item_id',
  'chat_id',
  'communication_id',
  'company_id',
  'contact_id',
  'doc_id',
  'folder_id',
  'lead_id',
  'message_id',
  'note_id',
  'opportunity_id',
  'resource_id',
  'service_id',
  'session_id',
  'sheet_id',
  'site_id',
  'site_version_id',
  'space_id',
  'sprint_id',
  'task_id',
  'user_id',
  'workflow_id',
  'workflow_instance_id',
  'workspace_id'
] as const

const SAFE_LABEL_KEYS = ['activity_type', 'plan', 'source'] as const

const CONTENT_FIELD_NAMES = new Set([
  'body',
  'content',
  'customer_email',
  'customer_phone',
  'customer_notes',
  'description',
  'email',
  'file_contents',
  'html',
  'message',
  'message_text',
  'messageText',
  'note',
  'notes',
  'phone',
  'prompt',
  'prompt_text',
  'response',
  'response_text',
  'subject',
  'task_description',
  'text',
  'title'
])

const SAFE_IDENTIFIER_VALUE_PATTERN = /^[A-Za-z0-9_.:/-]+$/

function includesString<const Values extends readonly string[]>(
  values: Values,
  value: string
): value is Values[number] {
  return values.includes(value)
}

function looksLikePhoneNumber(value: string) {
  const digits = value.replace(/\D/g, '')
  if (digits.length < 7 || digits.length > 15) return false

  return /^[+\d().\-\s]+$/.test(value)
}

function readString(record: Record<string, unknown>, key: string) {
  if (CONTENT_FIELD_NAMES.has(key)) return null

  const value = record[key]
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

function readSafeIdentifier(record: Record<string, unknown>, key: string) {
  const value = readString(record, key)
  if (!value) return null
  if (value.length > 256) return null
  if (value.includes('@')) return null
  if (!SAFE_IDENTIFIER_VALUE_PATTERN.test(value)) return null
  if (looksLikePhoneNumber(value)) return null

  return value
}

function readSafeLabel(record: Record<string, unknown>, key: string) {
  const value = readString(record, key)
  if (!value) return null
  if (value.length > 128) return null
  if (value.includes('@')) return null

  return /^[A-Za-z0-9_./:-]+$/.test(value) ? value : null
}

function readBoolean(record: Record<string, unknown>, key: string) {
  const value = record[key]
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return null

  const normalized = value.trim().toLowerCase()
  if (normalized === 'true') return true
  if (normalized === 'false') return false

  return null
}

function readEnum<const Values extends readonly string[]>(
  record: Record<string, unknown>,
  key: string,
  values: Values
): Values[number] | null {
  const value = readString(record, key)
  if (!value) return null

  return includesString(values, value) ? value : null
}

function readOccurredAt(record: Record<string, unknown>) {
  const value = record.occurred_at ?? record.timestamp

  if (typeof value === 'string') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }

  return null
}

export function sanitizeMeaningfulOutcomeProperties(
  rawProperties: Record<string, unknown>
): MeaningfulOutcomeProperties | null {
  const surface = readEnum(
    rawProperties,
    'surface',
    MEANINGFUL_OUTCOME_SURFACES
  )
  const outcomeType = readEnum(
    rawProperties,
    'outcome_type',
    MEANINGFUL_OUTCOME_TYPES
  )
  const qualifyingAction = readEnum(
    rawProperties,
    'qualifying_action',
    MEANINGFUL_OUTCOME_ACTIONS
  )

  if (!surface || !outcomeType || !qualifyingAction) return null

  const properties: MeaningfulOutcomeProperties = {
    surface,
    outcome_type: outcomeType,
    qualifying_action: qualifyingAction
  }

  for (const key of SAFE_IDENTIFIER_KEYS) {
    const value = readSafeIdentifier(rawProperties, key)
    if (value) {
      properties[key] = value
    }
  }

  for (const key of SAFE_LABEL_KEYS) {
    const value = readSafeLabel(rawProperties, key)
    if (value) {
      properties[key] = value
    }
  }

  const channel = readEnum(
    rawProperties,
    'channel',
    MEANINGFUL_OUTCOME_CHANNELS
  )
  if (channel) properties.channel = channel

  const scope = readEnum(rawProperties, 'scope', MEANINGFUL_OUTCOME_SCOPES)
  if (scope) properties.scope = scope

  const fileScope = readEnum(
    rawProperties,
    'file_scope',
    MEANINGFUL_OUTCOME_SCOPES
  )
  if (fileScope) properties.file_scope = fileScope

  const isAnonymous = readBoolean(rawProperties, 'is_anonymous')
  if (isAnonymous !== null) properties.is_anonymous = isAnonymous

  const isFirst = readBoolean(rawProperties, 'is_first_meaningful_outcome')
  if (isFirst !== null) properties.is_first_meaningful_outcome = isFirst

  const occurredAt = readOccurredAt(rawProperties)
  if (occurredAt) properties.occurred_at = occurredAt

  return properties
}

export function buildMeaningfulOutcomeEvent(
  eventName: MeaningfulOutcomeEventName,
  properties: MeaningfulOutcomeProperties
): MeaningfulOutcomeAnalyticsEvent {
  const sanitized = sanitizeMeaningfulOutcomeProperties(properties)

  if (!sanitized) {
    throw new Error(`Invalid meaningful outcome properties for ${eventName}`)
  }

  return {
    event: eventName,
    properties: sanitized
  }
}
